import React, { useState, useCallback, useEffect } from 'react';
import { Box, Typography } from '@availity/element';
import { Drawer } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Button from '../../Utils/components/Button';
import Sidebar from './Sidebar';
import { ValueCard, InfoCard, ApiCard, QuickstartStep, DemoFlowCard, FeatureItem } from '../../Utils/components/Cards';
import { typographyStyles, boxStyles, themeColors } from '../../../static/constants/styles';
import { getCurrentEnv } from '../../../hooks/apiUrlResolver';
import hospitalUserIcon from '../../../static/assets/icons/hospital-user.svg';
import moneyBillIcon from '../../../static/assets/icons/money-bill.svg';
import codeIcon from '../../../static/assets/icons/code.svg';

const CURL_EXAMPLE = `curl https://av-api-platform-sbx.applicationplatform.aws.availity.com/mcp/messages   --request POST   --header 'Content-Type: application/json'   --header 'X-API-Key: <API_KEY>'   --data '{
    "jsonrpc": "2.0",
    "method": "tools/call",
    "params": {
      "name": "verify_coverage",
      "arguments": {
        "payer_identifier": "12345",
        "provider_npi": "1234567890",
        "member_identifier": "ABC123456789",
        "member_first_name": "John",
        "member_last_name": "Doe",
        "member_date_of_birth": "1985-03-15",
        "provider_last_name": "Smith",
        "provider_first_name": "John",
        "subscriber_relationship": "Self",
        "service_type_code": "30",
        "coverage_effective_date": "2024-01-01"
      }
    },
    "id": 2
  }'`;

const VALUE_CARDS = [
  { badge: 'Tool discovery built in', title: 'Designed for developer tooling', description: 'Exposes capabilities as discoverable tools, so automation, assistants, and scripted workflows are easier to build.' },
  { badge: 'Structured responses', title: 'Clear, actionable outputs', description: 'Standardized response objects and errors reduce time spent debugging and integrating.' },
  { badge: 'Coverages - Claims - Estimator', title: 'Composable Workflows', description: 'Supports more business-aligned interactions instead of low-level API calls.' }
];

const INFO_CARDS = [
  { icon: hospitalUserIcon, title: 'MCP host', description: "The LLM is contained within the MCP host or within an AI application or environment, such as an AI-powered IDE or conversational AI. As a user’s typical interaction point, this is where the MCP host uses the LLM to process requests that may require external data or tools." },
  { icon: moneyBillIcon, title: 'MCP client', description: "The MCP client, located within the MCP host, helps the LLM and MCP server communicate with each other. It translates the LLM's requests for the MCP and converts the MCP's replies for the LLM. It also finds and uses available MCP servers." },
  { icon: codeIcon, title: 'MCP server', description: 'The MCP server is the external service that provides context, data, or capabilities to the LLM. It enables LLMs to connect to external systems, like databases and web services, translating their responses into a format the LLM can understand, which helps developers provide diverse functionalities.' }
];

const FEATURE_ITEMS = [
  'Capability discovery built in: Programmatically list available tools, required inputs, and supported parameters.',
  'Predictable request patterns: Consistent payload structure makes calls easier to automate, test, and reuse.',
  'Structured responses & errors: Clear outputs and standardized error objects support resilient integrations and faster troubleshooting.',
  'Workflow-friendly by design: Compose multi-step journeys—coverage → estimate without reworking integration patterns.',
  'Documentation that matches behavior: Examples map directly to tool schemas, so developers can copy, run, and iterate quickly.'
];

const QUICKSTART_STEPS = [
  { number: '1', title: 'Sign Up for Early Access to receive access details and onboarding instructions', description: 'Register for early access to the MCP Server and receive your API credentials and onboarding documentation.' },
  { number: '2', title: 'Configure and authenticate your MCP client', description: 'Configure your MCP client to connect to Availity’s MCP server and authenticate using an API key for seamless integration.' },
  { number: '3', title: 'Discover available tools to find the capability you need', description: 'List tools using MCP method tools/list to find the capability you need.' },
  { number: '4', title: 'Invoke a tool using the standard call pattern', description: 'Make your first API call using the discovered tool name and required input parameters.' },
  { number: '5', title: 'Add context to your MCP client so it can orchestrate MCP tools end-to-end', description: 'Provide the necessary context to your MCP client so it can integrate and orchestrate MCP tools within your workflow.' }
];

const GRID_LAYOUT = { 
  display: 'grid', 
  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, 
  gap: 3, 
  maxWidth: 1200,
  width: '100%'
};

const AIManagementTab = ({ theme, sidebarOpen, onSidebarClose }: { theme: 'light' | 'dark'; sidebarOpen: boolean; onSidebarClose: () => void }) => {
  const navigate = useNavigate();
  const [expandedSection, setExpandedSection] = useState<string | null>('mcp-server');
  const [activeContent, setActiveContent] = useState<string>('model-context-protocol');
  const [cloudfrontUrl, setCloudfrontUrl] = useState<string>('');

  const toggleSection = useCallback((section: string) => {
    setExpandedSection((prev) => (prev === section ? null : section));
  }, []);

  const handleMenuItemClick = useCallback((contentId: string) => {
    setActiveContent(contentId);
  }, []);


  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });

    const CLOUDFRONT_URL: Record<string, string> = {
      sbx: 'd21s4w8l0eg2h.cloudfront.net',
      tst: 'd1cxc9m6vg990f.cloudfront.net',
      qua: 'd1cxc9m6vg990f.cloudfront.net',
      qap: 'd1cxc9m6vg990f.cloudfront.net',
    };

    const currentEnv = getCurrentEnv() ?? 'sbx';
    setCloudfrontUrl(CLOUDFRONT_URL[currentEnv] ?? '');
  }, []);

  return (
    <Box sx={{ display: 'flex', height: '100%', bgcolor: themeColors[theme].background, fontFamily: 'Lato' }}>
      <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
        <Sidebar
          expandedSection={expandedSection}
          activeContent={activeContent}
          onToggleSection={toggleSection}
          onMenuItemClick={handleMenuItemClick}
          theme={theme}
        />
      </Box>

      <Drawer
        anchor="left"
        open={sidebarOpen}
        onClose={onSidebarClose}
        sx={{ display: { xs: 'block', lg: 'none' }, '& .MuiDrawer-paper': { width: 280 } }}
      >
        <Sidebar
          expandedSection={expandedSection}
          activeContent={activeContent}
          onToggleSection={toggleSection}
          onMenuItemClick={(id) => { handleMenuItemClick(id); onSidebarClose(); }}
          theme={theme}
        />
      </Drawer>

      {/* Main Content */}
      <Box sx={{ flex: 1, p: { xs: '24px 16px', md: '48px 32px', lg: '48px 64px' }, overflowY: 'auto', overflowX: 'hidden', display: 'flex', flexDirection: 'column', alignItems: 'center', maxWidth: '100%' }}>
        <Box id="model-context-protocol">
          <Typography
            sx={{ mb: 2, color: themeColors[theme].textMuted, fontSize: '16px', fontFamily: 'Lato', fontWeight: 600, lineHeight: '100%' }}
          >
            Model Context Protocol
          </Typography>

          <Typography component="h1" sx={{ ...typographyStyles.h1, mb: 3, color: themeColors[theme].textSecondary }}>
            Model Context Protocol Server for Availity APIs
          </Typography>

          <Typography sx={{ ...typographyStyles.body, mb: 6, ...boxStyles.section, color: themeColors[theme].textSecondary }}>
            A MCP server provides an interaction layer over Availity APIs that allows clients to explore available capabilities, invoke supported actions, and receive standardized responses through a consistent MCP protocol, making Availity services easier to discover, integrate, and automate.
          </Typography>

          <Typography component="h2" sx={{ ...typographyStyles.h2, mb: 4, color: themeColors[theme].textHeading }}>
            Key MCP Features
          </Typography>

          <Box sx={GRID_LAYOUT}>
            {VALUE_CARDS.map((card) => (
              <ValueCard key={card.title} {...card} theme={theme} />
            ))}
          </Box>
        </Box>

        <Box id="what-is-mcp">
          <Typography component="h2" sx={{ ...typographyStyles.h2, mt: 8, mb: 3, color: themeColors[theme].textHeading }}>
            What is MCP?
          </Typography>

          <Typography sx={{ ...typographyStyles.body, mb: 6, ...boxStyles.section, color: themeColors[theme].textBody }}>
            The Model Context Protocol (MCP) is a standardized protocol that enables AI models to interact with external
            tools and resources in a structured way. Think of it as a bridge between your AI models and the real world,
            allowing your AI models to access databases, APIs, file systems, and other external services through a 
            consistent interface. It supports multiple transport mechanisms to provide flexibility across different 
            environments.
          </Typography>

          <Box sx={GRID_LAYOUT}>
            {INFO_CARDS.map((card) => (
              <InfoCard key={card.title} {...card} theme={theme} />
            ))}
          </Box>
        </Box>

        <Box id="availity-mcp-server">
          <Typography component="h2" sx={{ ...typographyStyles.h2, mt: 8, mb: 3, color: themeColors[theme].textBody }}>
            Availity MCP Server
          </Typography>

          <Typography component="h3" sx={{ ...typographyStyles.h3, mb: 2, color: themeColors[theme].textSubheading }}>
            What is available to you via Availity MCP Server?
          </Typography>

          <Typography sx={{ ...typographyStyles.body, mb: 3, maxWidth: 1200, color: themeColors[theme].textBody }}>
            The MCP Server is Availity’s Model Context Protocol endpoint that exposes API capabilities as discoverable 
            tools. Your client can list tools, inspect required inputs, and invoke actions using a consistent call pattern.
          </Typography>

          <Typography sx={{ ...typographyStyles.body, mb: 6, maxWidth: 1200, color: themeColors[theme].textBody }}>
            Instead of learning different conventions across services, MCP provides a surface that helps teams 
            build repeatable workflows spanning Coverages, Patient Cost Estimator, and Claims etc.
          </Typography>

          <Typography component="h2" sx={{ ...typographyStyles.h2, mb: 3, color: themeColors[theme].textBody }}>
            Why choose MCP?
          </Typography>

          <Typography sx={{ ...typographyStyles.body, mb: 4, maxWidth: 1200, color: themeColors[theme].textBody }}>
            MCP standardizes how capabilities are discovered and invoked, so integrations are faster to implement and 
            easier to maintain.
          </Typography>

          <Box sx={{ maxWidth: 1200, mb: 8 }}>
            {FEATURE_ITEMS.map((text) => (
              <FeatureItem key={text.slice(0, 30)} text={text} isLast={text === FEATURE_ITEMS[FEATURE_ITEMS.length - 1]} theme={theme} />
            ))}
          </Box>
        </Box>

        <Box id="supported-api">
          <Typography component="h2" sx={{ ...typographyStyles.h2, mt: 8, mb: 3, color: themeColors[theme].textBody }}>
            Supported APIs
          </Typography>

          <Typography sx={{ ...typographyStyles.subtitle, mb: 6, maxWidth: 1200, color: themeColors[theme].textSubheading }}>
            Use MCP tools to interact with the most common workflows across three core Availity APIs.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 2, width: '100%', maxWidth: 1200 }}>
            <ApiCard
              title="Coverages"
              description="Eligibility and benefits workflows exposed as MCP tools so you can validate coverage with consistent inputs and outputs."
              items={[
                'Eligibility checks and coverage validation',
                'Benefit details and member context',
                'Common "first call" patterns',
              ]}
              buttonText="Explore Coverages capabilities"
              onButtonClick={() => navigate('/docs/overview', { state: { scrollToTag: 'coverages' } })}
              theme={theme}
            />
            <ApiCard
              title="Patient Cost Estimator"
              description="Create estimate workflows with structured tool outputs that support transparency and orchestration, 
              shifting data quality left and enabling pre-service workflow enablement."
              items={[
                'Estimate requests for services and members',
                'Structured cost breakdown outputs',
                'Workflow-ready responses',
              ]}
              buttonText="Explore Estimator capabilities"
              onButtonClick={() => navigate('/docs/overview', { state: { scrollToTag: 'patient-cost-estimator' } })}
              theme={theme}
            />
            <ApiCard
              title="Claim Statuses"
              description="Claim status workflows are represented as MCP tools that surface the lifecycle and adjudication state of a claim, 
              enabling consumers to retrieve timely status, payment, and exception details through structured tool inputs and predictable outputs."
              items={[
                'Claim status lookup and tracking',
                'Adjudication and lifecycle details',
                'Service line–level status',
              ]}
              buttonText="Explore Claim Statuses capabilities"
              onButtonClick={() => navigate('/docs/overview', { state: { scrollToTag: 'claim-status' } })}
              theme={theme}
            />
          </Box>
        </Box>

        <Box id="quick-start">
          <Typography component="h2" sx={{ ...typographyStyles.h2, mt: 8, mb: 3, color: themeColors[theme].textBody }}>
            Getting Started with MCP
          </Typography>

          <Typography sx={{ ...typographyStyles.bodyBold, mb: 6, maxWidth: 1200, color: themeColors[theme].textBody }}>
            Go from discovery to first successful call using a single MCP pattern.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 2.5, width: '100%', maxWidth: 1200 }}>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '39px' }}>
              {QUICKSTART_STEPS.map((step) => (
                <QuickstartStep key={step.number} {...step} theme={theme} />
              ))}
            </Box>

            <Box sx={{ ...boxStyles.codeBox, flex: 1, alignSelf: 'flex-start', border: `1px solid ${themeColors[theme].border}` }}>
              <Box sx={{ ...boxStyles.codeHeader, bgcolor: themeColors[theme].codeBackground }}>
                <Typography sx={{ ...typographyStyles.body, color: themeColors[theme].text }}>Example Invocation</Typography>
              </Box>
              <Box component="pre" sx={{ ...boxStyles.codeContent, width: '100%', color: themeColors[theme].codeText, borderTop: `1px solid ${themeColors[theme].border}`, whiteSpace: 'pre-wrap', overflow: 'visible', m: 0 }}>
{CURL_EXAMPLE}
              </Box>
            </Box>
          </Box>
        </Box>

        <Box id="demo-flow" sx={{ width: '100%', maxWidth: 1200, display: 'flex', flexDirection: 'column', gap: 4, mt: 8 }}>
          <Box sx={{ maxWidth: 1200, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography component="h2" sx={{ ...typographyStyles.h2, color: themeColors[theme].textBody }}>
              Example workflow: Coverage → Estimate
            </Typography>

            <Typography sx={{ ...typographyStyles.body, maxWidth: 1200, color: themeColors[theme].textBody }}>
              The following is a practical workflow showing how MCP helps orchestrate multiple Availity capabilities using different tools.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 2, width: '100%', maxWidth: 1200 }}>
            <DemoFlowCard
              number="1"
              title="Verify coverage"
              description="Confirm eligibility and benefits for the member and payer context"
              tags={['coverageStatus', 'planDetails', 'benefitSummary']}
              theme={theme}
            />
            <DemoFlowCard
              number="2"
              title="Generate an estimate"
              description="Retrieve a structured estimate response for the requested service."
              tags={['estimateTotal', 'lineItems', 'memberResponsibility']}
              theme={theme}
            />
          </Box>

          <Box
            component="video"
            controls
            preload="metadata"
            title="API Marketplace MCP Coverages Demo"
            src={cloudfrontUrl ? `https://${cloudfrontUrl}/Videos/API-Marketplace_MCP-Coverages-Demo.mov` : undefined}
            sx={{
              width: '100%',
              maxWidth: 1200,
              aspectRatio: '16 / 9',
              borderRadius: '8px',
              border: `6px solid ${themeColors[theme].videoBorder}`,
              display: 'block',
            }}
          />
        </Box>

        <Box sx={{ width: '100%', maxWidth: 1200, display: 'flex', flexDirection: 'column', gap: 5, padding: '40px 0', alignItems: 'flex-start' }}>
          <Box sx={{ maxWidth: 1200, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography sx={{ ...typographyStyles.cta, color: themeColors[theme].textBodyWhite }}>Sign Up for Early Access</Typography>

            <Typography sx={{ ...typographyStyles.bodyLarge, maxWidth: 1200, color: themeColors[theme].textBodyWhite }}>
              Sign up to receive access details and onboarding instructions for the MCP Server experience.
            </Typography>
          </Box>

          <Button variant="navbar" onClick={() => navigate('/join-early')}>Get Early Access</Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AIManagementTab;
